import { useState } from "react";
import api from "../lib/api";
import { Button } from "./ui/button";
import { Switch } from "./ui/switch";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "./ui/dialog";
import { Share2, Copy, Check, Globe, Lock } from "lucide-react";
import { toast } from "sonner";

export default function ShareDialog({ open, onOpenChange, journal, onUpdate }) {
  const [copied, setCopied] = useState(false);
  const shareUrl = journal?.share_url
    ? journal.share_url
    : journal?.share_id
      ? `${window.location.origin}/share/${journal.share_id}`
      : "";

  const togglePublic = async (next) => {
    try {
      const { data } = await api.post(`/journals/${journal.id}/share`, { is_public: next });
      onUpdate?.({
        ...journal,
        is_public: data.is_public,
        share_id: data.share_id,
        share_url: data.share_url,
      });
      toast.success(next ? "Public sharing enabled" : "Sharing disabled");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    }
  };

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error("Couldn't copy");
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-zinc-900 border-zinc-800 text-zinc-50">
        <DialogHeader>
          <DialogTitle className="font-display flex items-center gap-2">
            <Share2 className="w-4 h-4" /> Share journal
          </DialogTitle>
          <DialogDescription className="text-zinc-400">
            Generate a read-only public link for your slideshow.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-5">
          <div className="flex items-center justify-between bg-zinc-950 border border-zinc-800 rounded-md p-4">
            <div className="flex items-center gap-3">
              {journal?.is_public
                ? <Globe className="w-5 h-5 text-emerald-400" />
                : <Lock className="w-5 h-5 text-zinc-500" />}
              <div>
                <div className="font-medium">
                  {journal?.is_public ? "Anyone with the link" : "Only you"}
                </div>
                <div className="text-xs text-zinc-400">
                  {journal?.is_public
                    ? "Public read-only slideshow. No sign-in required."
                    : "Toggle on to generate a sharable link."}
                </div>
              </div>
            </div>
            <Switch
              data-testid="share-public-toggle"
              checked={!!journal?.is_public}
              onCheckedChange={togglePublic}
            />
          </div>

          {journal?.is_public && (
            <div className="space-y-2">
              <Label className="label-eyebrow">Public URL</Label>
              <div className="flex items-center gap-2">
                <Input
                  data-testid="share-url-input"
                  readOnly
                  value={shareUrl}
                  className="bg-zinc-950 border-zinc-800 font-mono text-xs"
                />
                <Button
                  data-testid="share-copy-btn"
                  type="button"
                  onClick={copy}
                  className="bg-blue-600 hover:bg-blue-500 shrink-0"
                >
                  {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                </Button>
              </div>
              <p className="text-xs text-zinc-500">
                Send this to your audience — opens directly in the fullscreen player.
              </p>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
